"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Building2, House as Home, ShieldCheck, ArrowUpRight, ArrowRight, HardHat, CircleCheck as CheckCircle2, MapPin, Umbrella, Layers } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
export default function GuildfordCoverage() {
  const shouldReduceMotion = useReducedMotion();
  const sectorBadges = ["GU1-GU4 postcodes", "Town-centre commercial", "Office developments", "Residential areas", "Refurbishment & mixed-use", "Roof projects"];
  const residentialFeatures = ["Roof repairs & structural maintenance", "Multi-storey extensions & loft conversions", "Chimney stack access & rebuilds", "External rendering & repointing"];
  const commercialFeatures = ["Main contractors & developer partnerships", "Commercial refurbishment & façade works", "Occupied sites with public protection", "Pavement licences & out-of-hours erection"];
  const roofingServices = [{
    title: "Roofing Scaffolding",
    description: "Edge-to-ridge platforms built for roofers, solar teams, and re-tiling works.",
    to: "/RoofingScaffoldingPage",
    icon: HardHat
  }, {
    title: "Temporary Roofs",
    description: "Full weather protection canopies and tin hats for sensitive strip-and-rebuild jobs.",
    to: "/TemporaryRoofsPage",
    icon: Umbrella
  }, {
    title: "Edge Protection",
    description: "Class-compliant guardrails, handrails, and debris netting keeping trades safe at height.",
    to: "/EdgeProtectionPage",
    icon: ShieldCheck
  }];
  const nearbyAreas = [{
    name: "Woking",
    to: "/scaffolding-in-woking"
  }, {
    name: "Farnham",
    to: "/scaffolding-in-farnham"
  }, {
    name: "Camberley",
    to: "/scaffolding-in-camberley"
  }];
  return <section id="guildfordcoverageservices" data-nav="light" className="relative bg-background text-foreground py-20 lg:py-28 overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Header Block */}
        <div className="max-w-3xl mx-auto text-center mb-16">
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          ease: "easeOut"
        }} className="flex items-center justify-center gap-2 mb-4">
            <Badge variant="outline" className="px-3 py-1 font-medium text-xs tracking-wide">
              <MapPin className="size-3.5 text-primary" />
              Surrey Coverage
            </Badge>
          </motion.div>

          <motion.h2 initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.1,
          ease: "easeOut"
        }} className="text-3xl sm:text-4xl lg:text-5xl font-bold font-default tracking-tight text-foreground">
            Scaffolding services across Guildford
          </motion.h2>

          <motion.p initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.15,
          ease: "easeOut"
        }} className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed font-default">
            Operating across Guildford postcode districts GU1-GU4 (covering GU1, GU2, GU3, and GU4), we provide certified access built to NASC standards for town-centre commercial schemes, occupied office blocks, and residential projects.
          </motion.p>

          {/* Scope Badges */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.2,
          ease: "easeOut"
        }} className="flex flex-wrap items-center justify-center gap-2 mt-6">
            {sectorBadges.map((badge, i) => <span key={badge} data-index={i} className="inline-flex items-center px-3 py-1 rounded-lg text-xs font-medium bg-muted text-muted-foreground border border-border">
                {badge}
              </span>)}
          </motion.div>
        </div>

        {/* Two Main Columns: Residential & Commercial */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
          {/* Residential Section */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 24
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.1,
          ease: "easeOut"
        }}>
            <Card className="h-full border-border bg-card shadow-sm hover:shadow-md transition-[box-shadow] flex flex-col justify-between">
              <div>
                <CardHeader className="pb-4">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="p-2.5 rounded-lg bg-primary/10 text-primary">
                      <Home className="size-6" />
                    </div>
                    <div>
                      <CardTitle className="text-xl sm:text-2xl font-bold font-default">
                        Residential scaffolding in Guildford
                      </CardTitle>
                      <CardDescription className="text-sm font-default mt-1">
                        Safe, tidy access tailored for homes, extensions, and private works.
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-2">
                  <p className="text-sm text-muted-foreground leading-relaxed mb-6 font-default">
                    We supply and erect domestic scaffolds that protect your property while giving roofers, builders, and decorators stable work platforms across GU1, GU2, GU3, and GU4. Every structure is designed to suit tight garden boundaries and complex roof lines.
                  </p>

                  <div className="space-y-3 mb-8">
                    {residentialFeatures.map((item, i) => <div key={item} data-index={i} className="flex items-start gap-3">
                        <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                        <span className="text-sm text-foreground font-default">{item}</span>
                      </div>)}
                  </div>
                </CardContent>
              </div>

              <div className="px-6 pb-6 pt-2 border-t border-border flex flex-wrap gap-3 items-center">
                <Button asChild variant="default" size="sm">
                  <Link to="/ResidentialScaffoldingPage">
                    Residential scaffolding
                    <ArrowUpRight className="size-4" />
                  </Link>
                </Button>
                <Button asChild variant="outline" size="sm">
                  <Link to="/ChimneyScaffoldingPage">
                    Chimney scaffolding
                    <ArrowUpRight className="size-4" />
                  </Link>
                </Button>
              </div>
            </Card>
          </motion.div>

          {/* Commercial Section */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 24
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.2,
          ease: "easeOut"
        }}>
            <Card className="h-full border-border bg-card shadow-sm hover:shadow-md transition-[box-shadow] flex flex-col justify-between">
              <div>
                <CardHeader className="pb-4">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="p-2.5 rounded-lg bg-primary/10 text-primary">
                      <Building2 className="size-6" />
                    </div>
                    <div>
                      <CardTitle className="text-xl sm:text-2xl font-bold font-default">
                        Commercial scaffolding in Guildford
                      </CardTitle>
                      <CardDescription className="text-sm font-default mt-1">
                        Contractor support for high streets, offices, and live premises.
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-2">
                  <p className="text-sm text-muted-foreground leading-relaxed mb-6 font-default">
                    We manage the access logistics for commercial refurbishment, new developments, and occupied public buildings. Our site teams handle pavement licensing, pedestrian fans, and timed out-of-hours installs throughout Guildford.
                  </p>

                  <div className="space-y-3 mb-8">
                    {commercialFeatures.map((item, i) => <div key={item} data-index={i} className="flex items-start gap-3">
                        <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                        <span className="text-sm text-foreground font-default">{item}</span>
                      </div>)}
                  </div>
                </CardContent>
              </div>

              <div className="px-6 pb-6 pt-2 border-t border-border flex flex-wrap gap-3 items-center">
                <Button asChild variant="default" size="sm">
                  <Link to="/CommercialScaffoldingPage">
                    Commercial scaffolding
                    <ArrowUpRight className="size-4" />
                  </Link>
                </Button>
                <Button asChild variant="outline" size="sm">
                  <Link to="/MainContractorsDevelopersPage">
                    Main contractors & developers
                    <ArrowUpRight className="size-4" />
                  </Link>
                </Button>
              </div>
            </Card>
          </motion.div>
        </div>

        {/* Roofing & Trade Access Block */}
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1
      } : {
        opacity: 0,
        y: 24
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: 0.25,
        ease: "easeOut"
      }} className="rounded-xl border border-border bg-card p-6 sm:p-8 lg:p-10 shadow-sm">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
            <div>
              <div className="inline-flex items-center gap-2 text-primary text-xs font-semibold uppercase tracking-wider mb-2">
                <Layers className="size-4" />
                Specialist Solutions
              </div>
              <h3 className="text-2xl sm:text-3xl font-bold font-default text-foreground">
                Roofing & trade access
              </h3>
              <p className="text-sm sm:text-base text-muted-foreground font-default mt-1">
                Engineered access packages built for roofing contractors, solar installers, and high-level trades.
              </p>
            </div>
            <Button asChild variant="outline" size="sm" className="self-start md:self-auto shrink-0">
              <Link to="/ContactRequestQuotePage">
                Request Guildford quote
                <ArrowUpRight className="size-4" />
              </Link>
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {roofingServices.map((service, i) => {
            const IconComp = service.icon;
            return <div key={service.title} data-index={i} className="rounded-lg border border-border bg-background/50 p-6 flex flex-col justify-between hover:border-primary/50 transition-[border-color] duration-200">
                  <div>
                    <div className="size-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center mb-4">
                      <IconComp className="size-5" />
                    </div>
                    <h4 className="text-lg font-semibold font-default text-foreground mb-2">
                      {service.title}
                    </h4>
                    <p className="text-sm text-muted-foreground font-default leading-relaxed mb-6">
                      {service.description}
                    </p>
                  </div>
                  <Button asChild variant="ghost" size="sm" className="px-0 hover:bg-transparent text-primary hover:text-primary/80 justify-start">
                    <Link to={service.to}>
                      Explore service
                      <ArrowUpRight className="size-4 ml-1" />
                    </Link>
                  </Button>
                </div>;
          })}
          </div>
        </motion.div>

        {/* Nearby Coverage Areas & Postcode Districts */}
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1
      } : {
        opacity: 0,
        y: 24
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: 0.3,
        ease: "easeOut"
      }} className="mt-8 rounded-xl border border-border bg-card p-6 sm:p-8 shadow-sm flex flex-col lg:flex-row lg:items-center justify-between gap-6">
          <div>
            <div className="inline-flex items-center gap-2 text-primary text-xs font-semibold uppercase tracking-wider mb-2">
              <MapPin className="size-4" />
              Surrounding Coverage
            </div>
            <h3 className="text-xl sm:text-2xl font-bold font-default text-foreground">
              Guildford postcodes & nearby areas
            </h3>
            <p className="text-sm text-muted-foreground font-default mt-1 max-w-2xl leading-relaxed">
              Serving the Guildford postcode districts of GU1-GU4 alongside rapid dispatch to active project sites across nearby towns.
            </p>
            <div className="flex flex-wrap items-center gap-2.5 mt-4">
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mr-1">
                Nearby areas:
              </span>
              {nearbyAreas.map((area, i) => <Button key={area.name} data-index={i} asChild variant="outline" size="sm" className="hover:border-primary/60">
                  <Link to={area.to}>
                    {area.name}
                    <ArrowUpRight className="size-3.5" />
                  </Link>
                </Button>)}
            </div>
          </div>
          <div className="shrink-0 pt-2 lg:pt-0">
            <Button asChild variant="default" size="default">
              <Link to="#">
                View all areas
                <ArrowRight className="size-4 ml-1" />
              </Link>
            </Button>
          </div>
        </motion.div>
      </div>
    </section>;
}